import { Injectable, inject } from '@angular/core';
import { TranslationService, SUPPORTED_LOCALES, type SupportedLocale } from '@kanek/shared-i18n';
import { AppStore, ClientContextService, DataService } from '@kanek/shared-data-access';

/**
 * Service responsible for managing language selection functionality in the Experience app.
 * Determines when to show the language selector and manages language switching.
 */
@Injectable({
  providedIn: 'root'
})
export class LanguageSelectorService {
  private translationService = inject(TranslationService);
  private clientContextService = inject(ClientContextService);
  private appStore = inject(AppStore);
  private dataService = inject(DataService);

  /**
   * Determine if the language selector should be displayed.
   * Shows when multiple translations are available (currently en-us and fr-ca)
   */
  shouldShowLanguageSelector(): boolean {
    // Show language selector for any supported locale since we now have both EN and FR translations
    // This allows users to switch between languages regardless of default
    return SUPPORTED_LOCALES.length > 1;
  }

  /**
   * Get available language options for the current campaign.
   * Filters supported locales based on what's available for this campaign.
   */
  getAvailableLanguages(): SupportedLocale[] {
    // For now, if language selector is shown, both EN and FR-CA are available
    if (this.shouldShowLanguageSelector()) {
      return SUPPORTED_LOCALES;
    }

    // Otherwise, only English is available
    return SUPPORTED_LOCALES.filter(locale => locale.code === 'en-us');
  }

  /**
   * Switch to a different language, update the in-memory session state, and persist
   * the preference to the session document so it survives page refreshes.
   */
  async switchLanguage(localeCode: string): Promise<void> {
    // Apply the locale change first (primary operation — if this fails, rethrow)
    await this.translationService.setLocale(localeCode);

    // Persist preference to session doc (fire-and-forget — don't block UI on error).
    // Only send the minimum required fields to avoid failing validateSessionFields.
    const session = this.appStore.session();
    if (session.id && session.short) {
      this.appStore.setSession({ ...session, locale: localeCode });
      this.dataService.updateSession({ id: session.id, short: session.short, locale: localeCode }).catch(error => {
        console.error('Failed to persist language preference to session:', error);
      });
    }
  }

  /**
   * Get the current selected language.
   */
  getCurrentLanguage(): SupportedLocale | undefined {
    const currentLocale = this.translationService.getCurrentLocaleValue();
    return SUPPORTED_LOCALES.find(locale => locale.code === currentLocale);
  }

  /**
   * Check if multiple languages are available for selection.
   */
  hasMultipleLanguages(): boolean {
    return this.getAvailableLanguages().length > 1;
  }
}